import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnInit,
  computed,
  signal,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DashboardService, SubscriptionPaymentApi } from '../dashboard.service';
import {
  FEATURE_GROUP_LABELS,
  FEATURE_GROUP_ORDER,
  FeatureGroup,
  MONTH_ABBR,
  PlanFeaturesApi,
  ResolvedFeature,
} from '../interfaces/dashboard.component';

@Component({
  selector: 'app-dashboard-subscription',
  standalone: false,
  templateUrl: './dashboard-subscription.html',
  styleUrl: './dashboard-subscription.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardSubscriptionComponent implements OnInit, OnChanges {
  @Input() isAdmin = false;
  @Input() canRequest = false; // doctor or admin — may raise "Expand your plan" requests
  @Input() subPayments: SubscriptionPaymentApi[] = [];
  @Input() loadingSubPayment = true;
  @Input() hasMorningAccess: boolean | null = null;

  readonly planFeaturesLoading = signal(true);
  readonly activePlanTab = signal<'clinic' | 'user'>('clinic');
  readonly clinicFeatures = signal<ResolvedFeature[]>([]);
  readonly userFeatures = signal<ResolvedFeature[]>([]);

  // The active tab's raw feature set (clinic vs. the viewer's own access).
  private readonly activeFeatures = computed<ResolvedFeature[]>(() =>
    this.activePlanTab() === 'clinic' ? this.clinicFeatures() : this.userFeatures()
  );

  // "What's Included" — only features flagged to show, grouped by category.
  readonly activeGroups = computed<FeatureGroup[]>(() =>
    this.groupByCategory(this.activeFeatures())
  );

  // "Expand your plan" — suggestable features this tab doesn't yet have.
  readonly suggestedFeatures = computed<ResolvedFeature[]>(() =>
    this.activeFeatures()
      .filter((f) => f.suggestable && !f.included)
      .sort((a, b) => a.sortOrder - b.sortOrder)
  );

  // Per-feature request state, keyed by feature key: 'sending' → 'done'.
  readonly requestState = signal<Record<string, 'sending' | 'done'>>({});
  readonly subPaymentModalUrl = signal<SafeResourceUrl | null>(null);
  readonly subPaymentsWithLabels = signal<
    (SubscriptionPaymentApi & { dateLabel: string; displayName: string })[]
  >([]);

  ngOnChanges(): void {
    this.subPaymentsWithLabels.set(
      this.subPayments.map((payment) => ({
        ...payment,
        dateLabel: this.formatSubPaymentDate(payment.date),
        displayName: payment.typeName ?? 'Subscription Payment',
      }))
    );
  }

  constructor(
    private dashboardService: DashboardService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.dashboardService.getPlanFeatures().subscribe({
      next: (data: PlanFeaturesApi) => {
        this.clinicFeatures.set(data.clinic ?? []);
        this.userFeatures.set(data.user ?? []);
        // Seed button state so features with an open request show "Requested"
        // across reloads (and after an admin reopens a handled request).
        const seeded: Record<string, 'sending' | 'done'> = {};
        for (const f of [...(data.clinic ?? []), ...(data.user ?? [])]) {
          if (f.requested) seeded[f.key] = 'done';
        }
        this.requestState.set(seeded);
        // Non-admins get no clinic bucket — default the tab to their own access.
        if (!data.clinic) this.activePlanTab.set('user');
        this.planFeaturesLoading.set(false);
      },
      error: () => this.planFeaturesLoading.set(false),
    });
  }

  openSubPayment(payment: SubscriptionPaymentApi): void {
    if (!payment.id) return;
    const url = `/api/dashboard/subscription/${payment.id}/pdf`;
    this.subPaymentModalUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(url));
  }

  closeSubPaymentModal(): void {
    this.subPaymentModalUrl.set(null);
  }

  // Send a "request to add" for a suggested feature; button locks to Sending → Requested.
  requestFeature(feature: ResolvedFeature): void {
    if (this.requestState()[feature.key]) return;
    this.requestState.update((s) => ({ ...s, [feature.key]: 'sending' }));
    this.dashboardService.requestFeature(feature.key).subscribe({
      next: () => this.requestState.update((s) => ({ ...s, [feature.key]: 'done' })),
      error: () =>
        this.requestState.update((s) => {
          const next = { ...s };
          delete next[feature.key];
          return next;
        }),
    });
  }

  private formatSubPaymentDate(date: string | null): string {
    if (!date) return '';
    const dt = new Date(date);
    return `${MONTH_ABBR[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
  }

  // Bucket resolved features into ordered category groups (included first
  // within each group). Labels/relevance already applied server-side.
  private groupByCategory(features: ResolvedFeature[]): FeatureGroup[] {
    return FEATURE_GROUP_ORDER.map((category) => ({
      category,
      label: FEATURE_GROUP_LABELS[category] ?? category,
      features: features
        .filter((f) => f.showOnDashboard && f.category === category)
        .sort((a, b) => {
          if (a.included !== b.included) return a.included ? -1 : 1;
          return a.sortOrder - b.sortOrder;
        }),
    })).filter((g) => g.features.length > 0);
  }
}
